import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePackagesStore } from "../stores/packagesStore";
import { useAuthStore } from "../stores/authStore";
import { supabase } from "../lib/supabase";
import { queryPackagesProtected } from "../lib/packagesApi";
import { queryOperatorsProtected } from "../lib/operatorsApi";
import type { Package, PackageItem, PackageCollaborator, Operator, PackageAuditLog } from "../types/database";

// ============================================
// PACKAGES
// ============================================

export function usePackages() {
  const { user, token } = useAuthStore();
  const { setPackages } = usePackagesStore();

  return useQuery({
    queryKey: ["packages", user?.id],
    queryFn: async () => {
      if (!user?.id || !token) return [];

      const packages = await queryPackagesProtected<Package[]>(token, "list", {});
      setPackages(packages);
      return packages;
    },
    enabled: !!user?.id && !!token,
  });
}

export function usePackage(id: string | null) {
  const { token } = useAuthStore();
  const { setSelectedPackage } = usePackagesStore();

  return useQuery({
    queryKey: ["package", id],
    queryFn: async () => {
      if (!id || !token) return null;

      const pkg = await queryPackagesProtected<Package>(token, "get", { id });
      setSelectedPackage(pkg);
      return pkg;
    },
    enabled: !!id && !!token,
  });
}

export function useCreatePackage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { addPackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (packageData: {
      name: string;
      description?: string;
      start_date?: string;
      end_date?: string;
      cover_image_url?: string;
    }) => {
      if (!token) throw new Error("User not authenticated");

      return await queryPackagesProtected<Package>(token, "create", packageData);
    },
    onSuccess: (data) => {
      addPackage(data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });
}

export function useUpdatePackage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Package> & { id: string }) => {
      if (!token) throw new Error("User not authenticated");

      return await queryPackagesProtected<Package>(token, "update", { id, ...updates });
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show the update
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

export function useDeletePackage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { removePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("User not authenticated");

      await queryPackagesProtected<{ id: string }>(token, "delete", { id });
      return id;
    },
    onSuccess: (id) => {
      removePackage(id);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
  });
}

// ============================================
// PACKAGE ITEMS
// ============================================

export function useAddPackageItem() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { addPackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async (item: Omit<PackageItem, "id" | "created_at">) => {
      if (!token) throw new Error("User not authenticated");

      const { package_id, ...itemData } = item;
      return await queryPackagesProtected<PackageItem>(token, "add_item", {
        package_id,
        ...itemData,
      });
    },
    onSuccess: (data) => {
      addPackageItem(data);
      queryClient.invalidateQueries({ queryKey: ["package", data.package_id] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show the new item
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.package_id] });
    },
  });
}

export function useUpdatePackageItem() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updatePackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async ({
      id,
      packageId,
      ...updates
    }: Partial<PackageItem> & { id: string; packageId: string }) => {
      if (!token) throw new Error("User not authenticated");

      const item = await queryPackagesProtected<PackageItem>(token, "update_item", {
        id,
        package_id: packageId,
        ...updates,
      });
      return { item, packageId };
    },
    onSuccess: ({ item, packageId }) => {
      updatePackageItem(item.id, item);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

export function useDeletePackageItem() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { removePackageItem } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ id, packageId }: { id: string; packageId: string }) => {
      if (!token) throw new Error("User not authenticated");

      await queryPackagesProtected<{ id: string }>(token, "delete_item", {
        id,
        package_id: packageId,
      });
      return { id, packageId };
    },
    onSuccess: ({ id, packageId }) => {
      removePackageItem(id);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show item removal
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", packageId] });
    },
  });
}

export function useReorderPackageItems() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { reorderPackageItems } = usePackagesStore();

  return useMutation({
    mutationFn: async ({
      packageId,
      items
    }: {
      packageId: string;
      items: PackageItem[];
    }) => {
      if (!token) throw new Error("User not authenticated");

      await queryPackagesProtected<{ package_id: string; items: PackageItem[] }>(token, "reorder_items", {
        package_id: packageId,
        items,
      });
      return { packageId, items };
    },
    onSuccess: ({ packageId, items }) => {
      reorderPackageItems(items);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

// ============================================
// PACKAGE COLLABORATORS
// ============================================

export function useInviteCollaborator() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { addCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({
      packageId,
      userId,
      role = "collaborator"
    }: {
      packageId: string;
      userId: string;
      role?: PackageCollaborator["role"];
    }) => {
      if (!token) throw new Error("User not authenticated");

      return await queryPackagesProtected<PackageCollaborator>(token, "invite_collaborator", {
        package_id: packageId,
        user_id: userId,
        role,
      });
    },
    onSuccess: async (data) => {
      addCollaborator(data);
      // Refetch queries and wait for completion
      await queryClient.refetchQueries({ queryKey: ["package", data.package_id] });
      await queryClient.refetchQueries({ queryKey: ["packages"] });
    },
  });
}

export function useRespondToInvite() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updateCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      packageId,
    }: {
      id: string;
      status: "accepted" | "rejected";
      packageId: string;
    }) => {
      if (!token) throw new Error("User not authenticated");

      const collaborator = await queryPackagesProtected<PackageCollaborator>(token, "respond_to_invite", {
        id,
        package_id: packageId,
        status,
      });
      return { collaborator, packageId };
    },
    onSuccess: ({ collaborator, packageId }) => {
      updateCollaborator(collaborator.id, collaborator);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      // Refresh audit log to show collaborator response
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", packageId] });
    },
  });
}

export function useRemoveCollaborator() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { removeCollaborator } = usePackagesStore();

  return useMutation({
    mutationFn: async ({ id, packageId }: { id: string; packageId: string }) => {
      if (!token) throw new Error("User not authenticated");

      await queryPackagesProtected<{ id: string }>(token, "remove_collaborator", {
        id,
        package_id: packageId,
      });
      return { id, packageId };
    },
    onSuccess: ({ id, packageId }) => {
      removeCollaborator(id);
      queryClient.invalidateQueries({ queryKey: ["package", packageId] });
    },
  });
}

// ============================================
// PUBLISH PACKAGE
// ============================================

export function usePublishPackage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("User not authenticated");

      // Check if there are pending collaborators
      const { data: collaborators } = await supabase
        .from("package_collaborators")
        .select("status")
        .eq("package_id", id)
        .neq("role", "owner");

      const hasPendingCollaborators = collaborators?.some(c => c.status === "pending");
      const newStatus = hasPendingCollaborators ? "pending_approval" : "active";

      return await queryPackagesProtected<Package>(token, "update", {
        id,
        status: newStatus,
        published_at: newStatus === "active" ? new Date().toISOString() : null,
      });
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show publish action
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

// ============================================
// UNPUBLISH PACKAGE
// ============================================

export function useUnpublishPackage() {
  const queryClient = useQueryClient();
  const { token } = useAuthStore();
  const { updatePackage } = usePackagesStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("User not authenticated");

      return await queryPackagesProtected<Package>(token, "update", {
        id,
        status: "draft",
        published_at: null,
      });
    },
    onSuccess: (data) => {
      updatePackage(data.id, data);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      queryClient.invalidateQueries({ queryKey: ["package", data.id] });
      // Refresh audit log to show unpublish action
      queryClient.invalidateQueries({ queryKey: ["package-audit-log", data.id] });
    },
  });
}

// ============================================
// OPERATOR SEARCH FOR PACKAGES
// ============================================

export function useSearchOperators(searchQuery: string, limit: number = 20) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: ["operators-search", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2 || !token) return [];

      return await queryOperatorsProtected<Operator[]>(token, "search", {
        query: searchQuery,
        limit,
      });
    },
    enabled: searchQuery.length >= 2 && !!token,
    staleTime: 30000, // 30 seconds
  });
}

// ============================================
// PACKAGE AUDIT LOG
// ============================================

export function usePackageAuditLog(packageId: string | null, limit: number = 50) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: ["package-audit-log", packageId],
    queryFn: async () => {
      if (!packageId || !token) return [];

      return await queryPackagesProtected<PackageAuditLog[]>(token, "audit_log", {
        package_id: packageId,
        limit,
      });
    },
    enabled: !!packageId && !!token,
  });
}

